/**
 * Payroll entry form: two-column layout
 * Left: Employee info, base salary, additions (earnings), deductions, notes
 * Right: Payment summary (real-time), Recent activity
 * Footer: Cancel, Save as Draft, Save and Confirm
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormRoot,
  FormTextarea,
  Input,
  toast,
  zodResolver,
} from "@devhop/ui";
import {
  ArrowLeftIcon,
  Calculator,
  CalendarOff,
  EyeIcon,
  HelpCircle,
  MessageSquare,
  MinusCircle,
  PlusCircle,
  PrinterIcon,
  RefreshCw,
  SparklesIcon,
  Trash2,
  User,
} from "lucide-react";
import { nanoid } from "nanoid";
import { useEffect, useState } from "react";
import * as RHF from "react-hook-form";
import { z } from "zod";
import type {
  PayrollDetailDTO,
  PayrollStatus,
} from "@/modules/hr/payroll/domain/types";
import { config } from "@/shared/lib/config";
import type { LookupItem } from "@/shared/lib/utils";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { payrollApi } from "../api/client";
import { payrollFormSamples } from "./payrollFormSamples";

const PayrollFormRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  amount: z.string(),
});
const PayrollFormSchema = z.object({
  employeeId: z.string().min(1, "ກະລຸນາເລືອກພະນັກງານ"),
  baseSalary: z.string().min(1, "ກະລຸນາປ້ອນເງິນເດືອນພື້ນຖານ"),
  otAmount: z.string(),
  bonusAmount: z.string(),
  earnings: z.array(PayrollFormRowSchema),
  deductions: z.array(PayrollFormRowSchema),
  notes: z.string(),
});

export type PayrollFormRow = { id: string; name: string; amount: string };

export type PayrollFormValues = {
  employeeId: string;
  baseSalary: string;
  otAmount: string;
  bonusAmount: string;
  earnings: PayrollFormRow[];
  deductions: PayrollFormRow[];
  notes: string;
};

function parseNum(s: string): number {
  const n = Number.parseFloat(String(s).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

/** Format number string for display with comma thousands separator (e.g. 1234567.5 → "1,234,567.50") */
function formatAmountDisplay(s: string): string {
  const trimmed = String(s).trim();
  if (trimmed === "") return "";
  const n = parseNum(trimmed);
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export function computeTotals(values: PayrollFormValues) {
  const base = parseNum(values.baseSalary);
  const ot = parseNum(values.otAmount);
  const bonus = parseNum(values.bonusAmount);
  const earningsSum = values.earnings.reduce(
    (sum, e) => sum + parseNum(e.amount),
    0,
  );
  const add = ot + bonus + earningsSum;
  const ded = values.deductions.reduce((sum, d) => sum + parseNum(d.amount), 0);
  const totalEarnings = base + add;
  const totalDeductions = ded;
  const netSalary = totalEarnings - totalDeductions;
  return {
    baseSalary: base,
    totalAdditions: add,
    totalDeductions: ded,
    totalEarnings,
    netSalary,
  };
}

/** Build items array for API: earnings first, then deductions, with orderIndex */
export function formValuesToItems(values: PayrollFormValues) {
  const items: Array<{
    type: "earning" | "deduction";
    name: string;
    amount: number;
    orderIndex: number;
  }> = [];
  values.earnings.forEach((e, i) => {
    const amount = parseNum(e.amount);
    if (e.name.trim() || amount > 0) {
      items.push({
        type: "earning",
        name: e.name.trim() || "ລາຍໄດ້",
        amount,
        orderIndex: i,
      });
    }
  });
  values.deductions.forEach((d, i) => {
    const amount = parseNum(d.amount);
    if (d.name.trim() || amount > 0) {
      items.push({
        type: "deduction",
        name: d.name.trim() || "ລາຍການຫັກ",
        amount,
        orderIndex: values.earnings.length + i,
      });
    }
  });
  return items;
}

const STATUS_LABEL: Record<PayrollStatus, string> = {
  draft: "ຮ່າງ",
  confirmed: "ຢືນຢັນ",
  paid: "ຊຳລະແລ້ວ",
};

const SUGGESTED_SOURCE_LABEL = {
  position_band: "ຈາກຊ່ວງເງິນເດືອນຕາມຕຳແໜ່ງ (ອາຍຸວຽກງານ)",
  promotion: "ຈາກປະຫວັດເລື່ອນຕຳແໜ່ງ",
  previous_payroll: "ຈາກເງິນເດືອນງວດກ່ອນ",
} as const;

type SuggestedPayrollSource = keyof typeof SUGGESTED_SOURCE_LABEL;

type OTPreviewSummary = { totalHours: number; otAmount: number };
type LeavePreviewSummary = { unpaidLeaveDays: number; deductionAmount: number };
type AdvanceRepaymentPreview = { totalAmount: number };

type PayrollEntryFormProps = {
  mode: "new" | "edit" | "view";
  payroll?: PayrollDetailDTO | null;
  periodYear?: number;
  periodMonth?: number;
  onCancel: () => void;
  onSaveDraft: (values: PayrollFormValues) => void;
  onSaveAndConfirm: (values: PayrollFormValues) => void;
  isSubmitting?: boolean;
};

const defaultFormValues: PayrollFormValues = {
  employeeId: "",
  baseSalary: "0",
  otAmount: "0",
  bonusAmount: "0",
  earnings: [],
  deductions: [],
  notes: "",
};

function payrollToFormValues(p: PayrollDetailDTO): PayrollFormValues {
  const byOrder = (a: { orderIndex: number }, b: { orderIndex: number }) =>
    a.orderIndex - b.orderIndex;
  const earnings: PayrollFormRow[] = p.items
    .filter((i) => i.type === "earning")
    .sort(byOrder)
    .map((i, idx) => ({
      id: `e-${idx}-${i.name}`,
      name: i.name,
      amount: String(i.amount),
    }));
  const deductions: PayrollFormRow[] = p.items
    .filter((i) => i.type === "deduction")
    .sort(byOrder)
    .map((i, idx) => ({
      id: `d-${idx}-${i.name}`,
      name: i.name,
      amount: String(i.amount),
    }));
  return {
    employeeId: p.employeeId,
    baseSalary: String(p.baseSalary),
    otAmount: String(p.otAmount ?? 0),
    bonusAmount: String(p.bonusAmount ?? 0),
    earnings,
    deductions,
    notes: p.notes ?? "",
  };
}

export function PayrollEntryForm({
  mode,
  payroll,
  periodYear: propPeriodYear,
  periodMonth: propPeriodMonth,
  onCancel,
  onSaveDraft,
  onSaveAndConfirm,
  isSubmitting,
}: PayrollEntryFormProps) {
  const [sampleIndex, setSampleIndex] = useState(0);
  const [otPreviewSummary, setOtPreviewSummary] =
    useState<OTPreviewSummary | null>(null);
  const [otPreviewLoading, setOtPreviewLoading] = useState(false);
  const [leavePreviewSummary, setLeavePreviewSummary] =
    useState<LeavePreviewSummary | null>(null);
  const [leavePreviewLoading, setLeavePreviewLoading] = useState(false);
  const [advanceRepaymentPreview, setAdvanceRepaymentPreview] =
    useState<AdvanceRepaymentPreview | null>(null);
  const [suggestedSalaryHint, setSuggestedSalaryHint] = useState<{
    source: SuggestedPayrollSource;
    amount: number;
    salaryGradeCode: string | null;
    salaryGradeLabel: string | null;
  } | null>(null);
  const periodYear = payroll?.periodYear ?? propPeriodYear;
  const periodMonth = payroll?.periodMonth ?? propPeriodMonth;
  const isDev =
    typeof import.meta !== "undefined" &&
    (import.meta.hot !== undefined || import.meta.env?.MODE === "development");
  const isNew = mode === "new";
  const isView = mode === "view";
  const readOnly = isView || payroll?.status === "paid";

  const defaultValues: PayrollFormValues = payroll
    ? payrollToFormValues(payroll)
    : { ...defaultFormValues, baseSalary: "0" };

  const methods = RHF.useForm<PayrollFormValues>({
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference
    resolver: zodResolver(PayrollFormSchema) as any,
    defaultValues,
    values: payroll ? payrollToFormValues(payroll) : undefined,
  });

  const employeeId =
    RHF.useWatch({ control: methods.control, name: "employeeId" }) ?? "";
  const earnings =
    RHF.useWatch({ control: methods.control, name: "earnings" }) ?? [];
  const deductions =
    RHF.useWatch({ control: methods.control, name: "deductions" }) ?? [];
  const baseSalaryStr =
    RHF.useWatch({ control: methods.control, name: "baseSalary" }) ?? "0";
  const otAmountStr =
    RHF.useWatch({ control: methods.control, name: "otAmount" }) ?? "0";
  const bonusAmountStr =
    RHF.useWatch({ control: methods.control, name: "bonusAmount" }) ?? "0";

  useEffect(() => {
    if (!isNew || !employeeId?.trim()) {
      setSuggestedSalaryHint(null);
      return;
    }
    let cancelled = false;
    setSuggestedSalaryHint(null);
    payrollApi
      .getSuggestedBaseSalary(employeeId.trim(), {
        periodYear: periodYear ?? undefined,
        periodMonth: periodMonth ?? undefined,
      })
      .then((res) => {
        if (cancelled) return;
        if (res.suggestedBaseSalary != null && res.suggestedBaseSalary > 0) {
          methods.setValue("baseSalary", String(res.suggestedBaseSalary));
          if (
            res.suggestedSource === "position_band" ||
            res.suggestedSource === "promotion" ||
            res.suggestedSource === "previous_payroll"
          ) {
            setSuggestedSalaryHint({
              source: res.suggestedSource,
              amount: res.suggestedBaseSalary,
              salaryGradeCode: res.salaryGradeCode ?? null,
              salaryGradeLabel: res.salaryGradeLabel ?? null,
            });
          }
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isNew, employeeId, methods, periodYear, periodMonth]);

  // Auto-fetch advance repayment preview เมื่อ employeeId + period พร้อม
  useEffect(() => {
    if (!isNew || !employeeId?.trim() || !periodYear || !periodMonth) {
      setAdvanceRepaymentPreview(null);
      return;
    }
    let cancelled = false;
    setAdvanceRepaymentPreview(null);
    payrollApi
      .getAdvanceRepaymentPreview({
        employeeId: employeeId.trim(),
        year: periodYear,
        month: periodMonth,
      })
      .then((res) => {
        if (cancelled) return;
        if (res.totalAmount > 0) setAdvanceRepaymentPreview(res);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isNew, employeeId, periodYear, periodMonth]);

  const totals = computeTotals({
    ...methods.getValues(),
    baseSalary: baseSalaryStr,
    otAmount: otAmountStr,
    bonusAmount: bonusAmountStr,
    earnings,
    deductions,
  });

  const addEarning = () => {
    methods.setValue("earnings", [
      ...earnings,
      { id: nanoid(), name: "", amount: "0" },
    ]);
  };
  const removeEarning = (index: number) => {
    methods.setValue(
      "earnings",
      earnings.filter((_, i) => i !== index),
    );
  };
  const updateEarning = (
    index: number,
    field: "name" | "amount",
    value: string,
  ) => {
    const next = [...earnings];
    const row = next[index];
    if (!row) return;
    next[index] = {
      id: row.id,
      name: field === "name" ? value : row.name,
      amount: field === "amount" ? value : row.amount,
    };
    methods.setValue("earnings", next);
  };

  const addDeduction = () => {
    methods.setValue("deductions", [
      ...deductions,
      { id: nanoid(), name: "", amount: "0" },
    ]);
  };
  const removeDeduction = (index: number) => {
    methods.setValue(
      "deductions",
      deductions.filter((_, i) => i !== index),
    );
  };
  const updateDeduction = (
    index: number,
    field: "name" | "amount",
    value: string,
  ) => {
    const next = [...deductions];
    const row = next[index];
    if (!row) return;
    next[index] = {
      id: row.id,
      name: field === "name" ? value : row.name,
      amount: field === "amount" ? value : row.amount,
    };
    methods.setValue("deductions", next);
  };

  const handleSaveDraft = methods.handleSubmit(onSaveDraft);
  const handleSaveAndConfirm = methods.handleSubmit(onSaveAndConfirm);

  const handleAutoFill = () => {
    const sample = payrollFormSamples[sampleIndex];
    if (sample) {
      setSuggestedSalaryHint(null);
      methods.setValue("baseSalary", sample.baseSalary);
      methods.setValue(
        "earnings",
        sample.earnings.map((e) => ({ ...e, id: nanoid() })),
      );
      methods.setValue(
        "deductions",
        sample.deductions.map((d) => ({ ...d, id: nanoid() })),
      );
      methods.setValue("notes", sample.notes);
      // Keep existing employeeId so user selection is preserved
      setSampleIndex((prev) => (prev + 1) % payrollFormSamples.length);
      toast.success("ເພີ່ມຂໍ້ມູນຕົວຢ່າງແລ້ວ. ກະລຸນາເລືອກພະນັກງານ.");
    }
  };

  const handleFetchOTFromSystem = async () => {
    const employeeId = methods.getValues("employeeId")?.trim();
    const baseSalary = parseNum(methods.getValues("baseSalary"));
    if (!employeeId) {
      toast.error("ກະລຸນາເລືອກພະນັກງານກ່ອນ");
      return;
    }
    if (!periodYear || !periodMonth) {
      toast.error("ບໍ່ມີຂໍ້ມູນໄລຍະເວລາ");
      return;
    }
    if (baseSalary <= 0) {
      toast.error("ກະລຸນາປ້ອນເງິນເດືອນພື້ນຖານກ່ອນ");
      return;
    }
    setOtPreviewLoading(true);
    setOtPreviewSummary(null);
    try {
      const result = await payrollApi.getOTPreview({
        employeeId,
        year: periodYear,
        month: periodMonth,
        baseSalary,
      });
      methods.setValue("otAmount", String(result.otAmount));
      setOtPreviewSummary({
        totalHours: result.totalHours,
        otAmount: result.otAmount,
      });
      toast.success(
        `ນຳ OT ຈາກລະບົບແລ້ວ: ${result.totalHours} ຊົ່ວໂມງ, ຈຳນວນເງິນ ${result.otAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`,
      );
    } catch {
      toast.error("ດຶງຂໍ້ມູນ OT ບໍ່ສຳເລັດ");
    } finally {
      setOtPreviewLoading(false);
    }
  };

  const LEAVE_DEDUCTION_LABEL = "ການລາທີ່ບໍ່ຮັບເງິນ";
  const handleFetchLeaveFromSystem = async () => {
    const employeeId = methods.getValues("employeeId")?.trim();
    const baseSalary = parseNum(methods.getValues("baseSalary"));
    if (!employeeId) {
      toast.error("ກະລຸນາເລືອກພະນັກງານກ່ອນ");
      return;
    }
    if (!periodYear || !periodMonth) {
      toast.error("ບໍ່ມີຂໍ້ມູນໄລຍະເວລາ");
      return;
    }
    if (baseSalary <= 0) {
      toast.error("ກະລຸນາປ້ອນເງິນເດືອນພື້ນຖານກ່ອນ");
      return;
    }
    setLeavePreviewLoading(true);
    setLeavePreviewSummary(null);
    try {
      const result = await payrollApi.getLeavePreview({
        employeeId,
        year: periodYear,
        month: periodMonth,
        baseSalary,
      });
      const existing = deductions.find(
        (d) => d.name.trim() === LEAVE_DEDUCTION_LABEL,
      );
      if (existing) {
        const idx = deductions.findIndex(
          (d) => d.name.trim() === LEAVE_DEDUCTION_LABEL,
        );
        if (idx >= 0)
          updateDeduction(idx, "amount", String(result.deductionAmount));
      } else {
        methods.setValue("deductions", [
          ...deductions,
          {
            id: nanoid(),
            name: LEAVE_DEDUCTION_LABEL,
            amount: String(result.deductionAmount),
          },
        ]);
      }
      setLeavePreviewSummary({
        unpaidLeaveDays: result.unpaidLeaveDays,
        deductionAmount: result.deductionAmount,
      });
      toast.success(
        result.unpaidLeaveDays > 0
          ? `ນຳການຫັກຈາກການລາມາໃຊ້: ${result.unpaidLeaveDays} ວັນ, ຫັກ ${result.deductionAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`
          : "ບໍ່ມີວັນລາທີ່ບໍ່ຮັບເງິນໃນເດືອນນີ້",
      );
    } catch {
      toast.error("ດຶງຂໍ້ມູນການຫັກຈາກການລາບໍ່ສຳເລັດ");
    } finally {
      setLeavePreviewLoading(false);
    }
  };

  return (
    <FormRoot<PayrollFormValues>
      methods={methods}
      onSubmit={() => {}}
      className="min-w-0"
    >
      <div className="flex gap-4">
        {/* Left column */}
        <div className="w-full">
          {/* Employee information */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="h-4 w-4" />
                ຂໍ້ມູນພະນັກງານ
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {isNew ? (
                <FormInfiniteCombobox<LookupItem>
                  name="employeeId"
                  label=""
                  requiredMark
                  queryKey={["users-lookup"]}
                  queryFn={(args) =>
                    fetchLookupForInfinite(
                      `${config.apiUrl}/users/lookup`,
                      args,
                    )
                  }
                  preloadQueryFn={(id) =>
                    hydrateLookupItem(`${config.apiUrl}/users/lookup`, id)
                  }
                  getLabel={(item) => item.name}
                  getValue={(item) => item.id}
                  placeholder="ເລືອກພະນັກງານ..."
                />
              ) : (
                <>
                  <div>
                    <span className="text-muted-foreground">ຊື່ພະນັກງານ: </span>
                    <span className="font-medium">
                      {payroll?.employeeName ?? "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">ລະຫັດພະນັກງານ: </span>
                    <span>{payroll?.employeeId ?? "—"}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">ພະແນກ: </span>
                    <span>{payroll?.departmentName ?? "—"}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">ຕໍາແໜ່ງ: </span>
                    <span>{payroll?.positionTitle ?? "—"}</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Base salary */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <User className="h-4 w-4" />
                ເງິນເດືອນພື້ນຖານ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <label
                  htmlFor="payroll-base-salary"
                  className="font-medium text-muted-foreground text-sm"
                >
                  ອັດຕາເງິນເດືອນຕໍ່ເດືອນ
                </label>
                <Input
                  id="payroll-base-salary"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={formatAmountDisplay(baseSalaryStr)}
                  onChange={(e) => {
                    setSuggestedSalaryHint(null);
                    methods.setValue(
                      "baseSalary",
                      e.target.value.replace(/,/g, ""),
                    );
                  }}
                  disabled={readOnly}
                  className="tabular-nums"
                />
                {isNew && suggestedSalaryHint != null ? (
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <Badge className="font-normal" variant="secondary">
                      <span className="flex flex-col items-start gap-0.5">
                        <span>
                          {SUGGESTED_SOURCE_LABEL[suggestedSalaryHint.source]}
                        </span>
                        {suggestedSalaryHint.source === "position_band" &&
                        (suggestedSalaryHint.salaryGradeLabel != null ||
                          suggestedSalaryHint.salaryGradeCode != null) ? (
                          <span className="font-normal text-[10px] opacity-80">
                            {[
                              suggestedSalaryHint.salaryGradeCode ?? "",
                              suggestedSalaryHint.salaryGradeLabel ?? "",
                            ]
                              .filter(Boolean)
                              .join(" — ")}
                          </span>
                        ) : null}
                      </span>
                    </Badge>
                    <span className="flex items-center gap-1 text-muted-foreground text-xs">
                      <HelpCircle className="h-3.5 w-3.5 shrink-0" />
                      ລະບົບເຕີມເງິນໃຫ້ຈາກຂໍ້ມູນ — ແກ້ໄດ້ດ້ວຍມືຕາມຕົກລົງ
                    </span>
                  </div>
                ) : null}
              </div>
            </CardContent>
          </Card>

          {/* ເງີນເພີ່ມອື່ນໆ: OT / ໂບນັສ */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <PlusCircle className="h-4 w-4 text-emerald-600" />
                ເງີນເພີ່ມອື່ນໆ
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
                <label
                  htmlFor="payroll-ot"
                  className="font-medium text-muted-foreground text-sm"
                >
                  OT (ເງິນລ່ວງເວລາ)
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    id="payroll-ot"
                    type="text"
                    inputMode="decimal"
                    placeholder="0"
                    value={formatAmountDisplay(otAmountStr)}
                    onChange={(e) => {
                      methods.setValue(
                        "otAmount",
                        e.target.value.replace(/,/g, ""),
                      );
                      setOtPreviewSummary(null);
                    }}
                    disabled={readOnly}
                    className="max-w-[180px] tabular-nums"
                  />
                  {!readOnly &&
                    Number.isInteger(periodYear) &&
                    Number.isInteger(periodMonth) && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleFetchOTFromSystem}
                        disabled={otPreviewLoading || isSubmitting}
                        className="gap-1.5"
                      >
                        <Calculator className="h-4 w-4" />
                        {otPreviewLoading ? "ກຳລັງດຶງ..." : "ນຳ OT ຈາກລະບົບ"}
                      </Button>
                    )}
                </div>
                {otPreviewSummary != null && (
                  <p className="text-muted-foreground text-xs">
                    ຊົ່ວໂມງ OT ທີ່ນຳມາ: {otPreviewSummary.totalHours} ຊົ່ວໂມງ
                    {otPreviewSummary.otAmount > 0 && (
                      <>
                        {" "}
                        · ຈຳນວນເງິນ{" "}
                        {otPreviewSummary.otAmount.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                        })}
                      </>
                    )}
                  </p>
                )}
              </div>
              <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
                <label
                  htmlFor="payroll-bonus"
                  className="font-medium text-muted-foreground text-sm"
                >
                  ໂບນັສ
                </label>
                <Input
                  id="payroll-bonus"
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  value={formatAmountDisplay(bonusAmountStr)}
                  onChange={(e) =>
                    methods.setValue(
                      "bonusAmount",
                      e.target.value.replace(/,/g, ""),
                    )
                  }
                  disabled={readOnly}
                  className="max-w-[180px] tabular-nums"
                />
              </div>
            </CardContent>
          </Card>

          {/* Additions (earnings) */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <PlusCircle className="h-4 w-4 text-emerald-600" />
                ລາຍໄດ້ເພີ່ມ
              </CardTitle>
              {!readOnly && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addEarning}
                >
                  + ເພີ່ມລາຍໄດ້
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {earnings.length === 0 ? (
                <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີລາຍໄດ້ເພີ່ມ</p>
              ) : (
                earnings.map((row, i) => (
                  <div
                    key={row.id}
                    className="flex flex-wrap items-center gap-2"
                  >
                    <Input
                      placeholder="ຊື່ລາຍການ"
                      value={row.name}
                      onChange={(e) => updateEarning(i, "name", e.target.value)}
                      disabled={readOnly}
                      className="min-w-[140px] flex-1"
                    />
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={formatAmountDisplay(row.amount)}
                      onChange={(e) =>
                        updateEarning(
                          i,
                          "amount",
                          e.target.value.replace(/,/g, ""),
                        )
                      }
                      disabled={readOnly}
                      className="w-28 tabular-nums"
                    />
                    {!readOnly && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeEarning(i)}
                        aria-label="ລຶບ"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Deductions */}
          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <MinusCircle className="h-4 w-4 text-destructive" />
                ລາຍການຫັກ
              </CardTitle>
              {!readOnly && (
                <div className="flex flex-wrap items-center gap-2">
                  {Number.isInteger(periodYear) &&
                    Number.isInteger(periodMonth) && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleFetchLeaveFromSystem}
                        disabled={leavePreviewLoading || isSubmitting}
                        className="gap-1.5"
                      >
                        <CalendarOff className="h-4 w-4" />
                        {leavePreviewLoading
                          ? "ກຳລັງດຶງ..."
                          : "ນຳການຫັກຈາກການລາມາໃຊ້"}
                      </Button>
                    )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addDeduction}
                  >
                    + ເພີ່ມລາຍການຫັກ
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {leavePreviewSummary != null &&
                leavePreviewSummary.unpaidLeaveDays > 0 && (
                  <p className="text-muted-foreground text-xs">
                    ວັນລາທີ່ບໍ່ຮັບເງິນ: {leavePreviewSummary.unpaidLeaveDays} ວັນ
                    {leavePreviewSummary.deductionAmount > 0 && (
                      <>
                        {" "}
                        · ຈຳນວນຫັກ{" "}
                        {leavePreviewSummary.deductionAmount.toLocaleString(
                          "en-US",
                          {
                            minimumFractionDigits: 2,
                          },
                        )}
                      </>
                    )}
                  </p>
                )}
              {deductions.length === 0 ? (
                <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີລາຍການຫັກ</p>
              ) : (
                deductions.map((row, i) => (
                  <div
                    key={row.id}
                    className="flex flex-wrap items-center gap-2"
                  >
                    <Input
                      placeholder="ຊື່ລາຍການ"
                      value={row.name}
                      onChange={(e) =>
                        updateDeduction(i, "name", e.target.value)
                      }
                      disabled={readOnly}
                      className="min-w-[140px] flex-1"
                    />
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={formatAmountDisplay(row.amount)}
                      onChange={(e) =>
                        updateDeduction(
                          i,
                          "amount",
                          e.target.value.replace(/,/g, ""),
                        )
                      }
                      disabled={readOnly}
                      className="w-28 tabular-nums"
                    />
                    {!readOnly && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeDeduction(i)}
                        aria-label="ລຶບ"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Payroll notes */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageSquare className="h-4 w-4" />
                ຫມາຍເຫດເງິນເດືອນ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FormTextarea
                name="notes"
                placeholder="ເພີ່ມຫມາຍເຫດ ຫຼື ການປັບແຕ່ງພາຍໃນສໍາລັບໄລຍະນີ້..."
                rows={3}
                disabled={readOnly}
              />
            </CardContent>
          </Card>
        </div>

        {/* Right column: Payment Summary + Recent Activity — follows page theme (light theme = light card, dark theme = dark card) */}
        <div className="max-w-md">
          {/* Payment Summary — uses card/foreground so it matches theme, with primary accent */}
          <Card className="overflow-hidden border-l-4 border-l-primary shadow-md">
            <CardHeader className="pt-6 pb-1">
              <CardTitle className="font-bold text-base">ສະຫຼຸບການຊຳລະ</CardTitle>
              <p className="text-muted-foreground text-xs uppercase tracking-wide">
                ຄິດໄລ່ແບບ real-time
              </p>
            </CardHeader>
            <CardContent className="space-y-2 pb-6">
              <div className="flex justify-between text-sm tabular-nums">
                <span className="text-muted-foreground">ເງິນເດືອນພື້ນຖານ</span>
                <span>
                  {totals.baseSalary.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
              <div className="flex justify-between text-emerald-600 text-sm tabular-nums dark:text-emerald-400">
                <span>ລາຍໄດ້ເພີ່ມລວມ</span>
                <span>
                  +
                  {totals.totalAdditions.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
              <div className="flex justify-between text-red-600 text-sm tabular-nums dark:text-red-400">
                <span>ລາຍການຫັກລວມ</span>
                <span>
                  -
                  {totals.totalDeductions.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
              {advanceRepaymentPreview &&
                advanceRepaymentPreview.totalAmount > 0 && (
                  <div className="flex items-start justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm dark:border-amber-800 dark:bg-amber-950/30">
                    <div className="min-w-0">
                      <p className="font-medium text-amber-800 text-xs dark:text-amber-300">
                        ຫັກຄືນເງິນເດືອນລ່ວງໜ້າ
                      </p>
                      <p className="mt-0.5 text-amber-600 text-xs dark:text-amber-400">
                        ລະບົບຈະຫັກໂດຍອັດຕະໂນມັດເມື່ອສ້າງ
                      </p>
                    </div>
                    <span className="shrink-0 font-medium text-amber-800 tabular-nums dark:text-amber-300">
                      -
                      {advanceRepaymentPreview.totalAmount.toLocaleString(
                        "en-US",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      )}
                    </span>
                  </div>
                )}
              <div className="mt-4 border-t pt-4">
                <p className="mb-1 font-bold text-muted-foreground text-sm uppercase tracking-wide">
                  ຍອດສຸດທິທີ່ຈະໄດ້ຮັບ
                </p>
                <p className="font-bold text-2xl text-primary tabular-nums">
                  {(
                    totals.netSalary -
                    (advanceRepaymentPreview?.totalAmount ?? 0)
                  ).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>
              <p className="mt-3 text-muted-foreground text-xs italic">
                ຫມາຍເຫດ: ຍອດດັ່ງກ່າວລວມທຸກລາຍການຫັກ ແລະ ລາຍໄດ້ທີ່ກຳນົດໃນເອກະສານນີ້.
              </p>
            </CardContent>
          </Card>

          {/* Recent Activity - card uses bg-card/text (theme-aware), list with primary dots */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <RefreshCw className="h-4 w-4" />
                ກິດຈະກໍາຫຼ້າສຸດ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-4">
                {payroll?.createdAt && (
                  <li className="flex gap-3">
                    <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
                    <div className="min-w-0 text-sm">
                      <p className="font-medium">ສ້າງຮ່າງ</p>
                      <p className="text-muted-foreground text-xs">
                        {new Date(payroll.createdAt).toLocaleString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}{" "}
                        {payroll.createdBy ? `by ${payroll.createdBy}` : ""}
                      </p>
                    </div>
                  </li>
                )}
                {payroll?.updatedAt &&
                  payroll.updatedAt !== payroll?.createdAt && (
                    <li className="flex gap-3">
                      <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
                      <div className="min-w-0 text-sm">
                        <p className="font-medium">ອັບເດດຂໍ້ມູນ</p>
                        <p className="text-muted-foreground text-xs">
                          {new Date(payroll.updatedAt).toLocaleString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}{" "}
                          {payroll.updatedBy ? `by ${payroll.updatedBy}` : ""}
                        </p>
                      </div>
                    </li>
                  )}
                {!payroll?.createdAt && (
                  <li className="flex gap-3">
                    <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-primary" />
                    <div className="min-w-0 text-sm">
                      <p className="font-medium">ຮ່າງໃໝ່</p>
                      <p className="text-muted-foreground text-xs">
                        ບໍ່ມີກິດຈະກໍາຫຼ້າສຸດ — ສ້າງບັນທຶກແລ້ວຈະສະແດງຢູ່ນີ້
                      </p>
                    </div>
                  </li>
                )}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer — sticky so save actions stay visible when scrolling */}
      <div className="sticky bottom-0 z-10 mt-8 flex flex-wrap items-center justify-between gap-4 border-t bg-background/95 py-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            ຍົກເລີກ
          </Button>
          {isDev && isNew && payrollFormSamples.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAutoFill}
              disabled={isSubmitting}
              className="gap-1.5"
            >
              <SparklesIcon className="h-4 w-4" />
              Auto Fill (Sample {sampleIndex + 1}/{payrollFormSamples.length})
            </Button>
          )}
        </div>
        {!readOnly && (
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleSaveDraft}
              disabled={isSubmitting}
            >
              ບັນທຶກຮ່າງ
            </Button>
            <Button
              type="button"
              onClick={handleSaveAndConfirm}
              disabled={isSubmitting}
            >
              ບັນທຶກແລະຢືນຢັນ
            </Button>
          </div>
        )}
      </div>
    </FormRoot>
  );
}

export function PayrollEntryHeader({
  status,
  periodLabel: _periodLabel,
  employeeName: _employeeName,
  title,
  subtitle,
  onBack,
  onPreview,
  onPrint,
  onStartTour,
}: {
  status?: PayrollStatus;
  periodLabel: string;
  employeeName?: string;
  title: string;
  subtitle: string;
  onBack?: () => void;
  /** Navigate to slip preview page (view only) */
  onPreview?: () => void;
  /** Navigate to slip preview page and trigger print (print runs on preview page) */
  onPrint?: () => void;
  /** Start guided tour */
  onStartTour?: () => void;
}) {
  return (
    <div className="mb-6">
      <div className="mt-2 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center space-x-2">
            <h1 className="font-semibold text-2xl">{title}</h1>
            {onStartTour != null && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 rounded-full"
                onClick={onStartTour}
                title="ເລີ່ມທົດສອບການນຳທາງ"
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-5" />
              </Button>
            )}
            {status != null && (
              <span
                className={
                  status === "paid"
                    ? "rounded-full bg-emerald-100 px-3 py-1 font-medium text-emerald-700 text-sm"
                    : status === "confirmed"
                      ? "rounded-full bg-blue-100 px-3 py-1 font-medium text-blue-700 text-sm"
                      : "rounded-full bg-amber-100 px-3 py-1 font-medium text-amber-700 text-sm"
                }
              >
                {STATUS_LABEL[status]}
              </span>
            )}
          </div>
          <p className="text-muted-foreground text-sm">{subtitle}</p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {onPreview != null && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onPreview}
              className="gap-1.5"
            >
              <EyeIcon className="h-4 w-4" />
              ເບິ່ງ
            </Button>
          )}
          {onPrint != null && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onPrint}
              className="gap-1.5"
            >
              <PrinterIcon className="h-4 w-4" />
              ພິມ
            </Button>
          )}
          {onBack != null && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="-ml-2 gap-2"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              ກັບຄືນ
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
